import React from 'react';
import { applicationSettings } from '../../settings/application';
import tacHelpers from '../../state/tac/tacHelpers';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import {
  KompisSummaryPage,
  KompisSummaryPageFooter,
} from '@inter-ikea-kompis/react-components';
import { ProductRowProductMeasureEnum } from '@inter-ikea-kompis/component-product-row';
import { ShareDesignCardCopyStateEnum } from '@inter-ikea-kompis/component-share-design-card';
import { getShoppingProductsFromTAC } from '../../util/aactools/kompisConvert';
import { useDispatch, useSelector } from 'react-redux';
import { selectTac } from '../../state/tac/tacSelectors';
import { selectStores } from '../../state/stores/storesSelectors';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import {
  selectDexfSettings,
  selectHasTipOverWarning,
} from '../../state/dexfSettings/dexfSettingsSelectors';
import { t } from '../../translations';
import { translate } from '../../services/L10n';
import constants from '../../settings/constants';
import { selectDirtyConfigurationState } from '../../state/vpc/vpcSelectors';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';
import {
  SummaryPageConfigurationMeasureEnum,
  SummaryPageModalEnum,
} from '@inter-ikea-kompis/component-summary-page';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';
import { ILinkCard } from '@inter-ikea-kompis/types';
import productService from '../../services/products';
import {
  selectAddToCartState,
  selectAddToListState,
  selectAddUnavailableProductsToList,
  selectConfirmationCardType,
  selectModalState,
  selectModalType,
  selectStoreAvailabilities,
  selectLocale,
  selectStoreId,
  selectDesignLink,
  selectShareDesignCard,
  selectIsDesignLinkEitherReadyOrFailed,
  selectIsAnyUserActionInProgress,
  selectFailedShoppingItems,
  selectHomeDelivery,
  selectFinancingOption,
} from '../../state/summary/summarySelectors';
import {
  actionClearButtons,
  actionSetAddUnavailableProductsToList,
  actionSetHideModal,
  actionSetModalType,
  actionSetShowModal,
  actionSetShareDesignCardState,
  actionSetCopyDesignCodeState,
  actionSetCopyDesignLinkState,
  actionSetSendByEmailState,
  actionSetHomeDeliveryState,
  actionSetHomeDeliveryInputValue,
  actionSetHomeDeliveryZipAvailabilities,
  actionSetHomeDeliveryZipCode,
} from '../../state/summary/summaryActions';
import {
  thunkOnAddUnavailableToCartAndList,
  thunkInitSummary,
  thunkOnAddToCart,
  thunkOnAddToList,
  thunkSetSelectedStore,
  thunkOnSendByEmailSend,
  thunkHandleHomeDelivery,
  thunkHandleFinancingOptions,
  thunkOnKioskMainCTA,
  thunkSetSummaryInstanceState,
  homeDeliveryModal,
} from '../../state/summary/summaryThunks';
import { thunkOnShareDesign } from '../../state/vpc/vpcThunks';
import {
  selectSavedVpcCode,
  selectSaveVpcState,
} from '../../state/vpc/vpcSelectors';
import LoadingSpinner from '../../components/LoadingSpinner';
import TopBar from '../../components/TopBar';
import topBarStyles from '../../components/TopBar/TopBar.module.less';
import styles from './SummaryPage.module.less';
import { Ymal } from './Ymal';
import {
  actionSetOverlayState,
  actionSetScrollException,
} from '../../state/popups/popupsActions';
import {
  selectIsKiosk,
  selectIsMobile,
  selectIsMobileOrTablet,
} from '../../state/userAgent/userAgentSelectors';
import { RANGES, ITEMS } from '../../constants';
import GoodToKnowTab from '../../components/GoodToKnowTab/GoodToKnowTab';
import { TacItem } from '../../generalTypes';
export interface Props {
  detailedSceneImage: string;
}

const SummaryPage: React.FC<Props> = ({ detailedSceneImage }) => {
  const tac = useSelector(selectTac);
  const stores = useSelector(selectStores);
  const kompisTranslations = useSelector(selectKompisTranslations);
  const dexfSettings = useSelector(selectDexfSettings);
  const hasTipOverWarning = useSelector(selectHasTipOverWarning);
  const locale = useSelector(selectLocale);
  const storeId = useSelector(selectStoreId);
  const storeAvailabilities = useSelector(selectStoreAvailabilities);
  const vpcCode = useSelector(selectSavedVpcCode);
  const designLink = useSelector(selectDesignLink);
  const confirmationCardType = useSelector(selectConfirmationCardType);
  const showModal = useSelector(selectModalState);
  const addToCartState = useSelector(selectAddToCartState);
  const addToListState = useSelector(selectAddToListState);
  const saveVpcState = useSelector(selectSaveVpcState);
  const modalType = useSelector(selectModalType);
  const dirtyConfiguration = useSelector(selectDirtyConfigurationState);
  const isMobile = useSelector(selectIsMobile);
  const isKiosk = useSelector(selectIsKiosk);
  const failedShoppingItems = useSelector(selectFailedShoppingItems);
  const isMobileOrTablet = useSelector(selectIsMobileOrTablet);
  const addUnavailableProductsToList = useSelector(
    selectAddUnavailableProductsToList
  );
  const zipCodeExists = useSelector(selectHomeDelivery).zipCode;
  const zipInCardState = useSelector(selectHomeDelivery).state;
  const shareDesignCard = useSelector(selectShareDesignCard);
  const zipAvailabilities = useSelector(selectHomeDelivery).zipAvailabilities;
  const financingOption = useSelector(selectFinancingOption);

  const isDesignLinkEitherReadyOrFailed = useSelector(
    selectIsDesignLinkEitherReadyOrFailed
  );
  const isAnyUserActionInProgress = useSelector(
    selectIsAnyUserActionInProgress
  );

  if (!tac) return null;

  const dispatch = useDispatch();

  const clearButtons = () => dispatch(actionClearButtons());
  const setSelectedStore = (storeId: string) =>
    dispatch(thunkSetSelectedStore(storeId));
  const setShowModal = () => dispatch(actionSetShowModal());
  const setHideModal = () => {
    dispatch(actionSetOverlayState(false));
    dispatch(actionSetHideModal());

    //Temp fix until kompis fix their problem
    onSendByEmailReset();
  };
  const setModalType = (visibleModal: any) =>
    dispatch(actionSetModalType(visibleModal));
  const onShareDesign = () => dispatch(thunkOnShareDesign());
  const onAddToCart = ({ detail }: any) =>
    dispatch(thunkOnAddToCart(detail, false));
  const onAddToList = () => dispatch(thunkOnAddToList(false, false));
  const onAddUnavailableToCartAndList = ({ detail }: any) =>
    dispatch(thunkOnAddUnavailableToCartAndList(detail));
  const onSendByEmailSend = ({ detail: { value } }: any) =>
    dispatch(thunkOnSendByEmailSend(value, vpcCode));
  const onHomeDeliverySubmitButtonClick = ({ detail: { zipCode } }: any) =>
    dispatch(thunkHandleHomeDelivery(zipCode));
  const onKioskMainCtaButtonClick = () => {
    dispatch(thunkOnKioskMainCTA());
  };

  const [shoppingProducts] = React.useState(getShoppingProductsFromTAC(tac));
  const [collectedProducts, setCollectedProducts] = React.useState([]);

  React.useEffect(() => {
    dispatch(thunkInitSummary());
    if (dirtyConfiguration) {
      clearButtons();
      zipCodeExists && dispatch(thunkHandleHomeDelivery());
    }
  }, []);

  React.useEffect(() => {
    dispatch(thunkHandleFinancingOptions(shoppingProducts));
  }, [shoppingProducts]);

  /**
   * Handles product collect change
   * @param event
   */
  const onCollectProduct = (event: any) =>
    setCollectedProducts(event.detail.collected);

  /**
   * Fetches modal
   */
  const getModal = () => (showModal ? modalType : undefined);

  /**
   * On modal open
   * @param visibleModal
   */
  const onModalOpen = ({ detail: { visibleModal } }: any) => {
    //FinancialServiceCard buttons are rendered far down due to our iframe, resize.
    const longModal =
      visibleModal.modal === SummaryPageModalEnum.financialServicesCard;
    const shouldSetOverlay = isMobileOrTablet || longModal;
    setModalType(visibleModal);
    setShowModal();
    isMobile && dispatch(actionSetScrollException(true));
    shouldSetOverlay && dispatch(actionSetOverlayState(true));
  };

  /**
   * On selected store change
   * @param storeId
   */
  const onStoreSelectorChange = ({ detail: { storeId } }: any) => {
    setSelectedStore(storeId);
    setHideModal();
  };

  /**
   * On add unavailable products to list change
   * @param addUnavailableProductsToList
   */
  const onAddUnavailableProductsToListChange = ({
    detail: { addUnavailableProductsToList },
  }: any) => {
    dispatch(actionSetOverlayState(true));
    dispatch(
      actionSetAddUnavailableProductsToList(addUnavailableProductsToList)
    );
  };

  /**
   * Handles home delivery forget click
   * @param detail
   */
  const onHomeDeliveryForgetClick = ({ detail }: any) => {
    setHideModal();
    dispatch(actionSetHomeDeliveryState(detail.state));
    dispatch(actionSetHomeDeliveryInputValue(detail.inputValue));
    dispatch(actionSetHomeDeliveryZipAvailabilities(detail.zipAvailabilities));
    dispatch(actionSetHomeDeliveryZipCode(detail.zipCode));
  };

  const onOpenZipIn = () => {
    dispatch(thunkSetSummaryInstanceState(homeDeliveryModal, {}));
  };

  /**
   * Handles email reset
   */
  const onSendByEmailReset = () => {
    dispatch(actionSetSendByEmailState(SendByEmailStateEnum.default));
  };

  const onShareDesignCardStateChange = ({ detail: { selectedState } }: any) => {
    dispatch(actionSetShareDesignCardState(selectedState));
  };

  const onCopyDesignCodeClick = ({ detail: { state } }: any) => {
    dispatch(actionSetCopyDesignCodeState(state));
  };

  const onCopyDesignCodeReset = () => {
    dispatch(
      actionSetCopyDesignCodeState(ShareDesignCardCopyStateEnum.default)
    );
  };

  const onCopyDesignLinkClick = ({ detail: { state } }: any) => {
    dispatch(actionSetCopyDesignLinkState(state));
  };

  const onCopyDesignLinkReset = () => {
    dispatch(
      actionSetCopyDesignLinkState(ShareDesignCardCopyStateEnum.default)
    );
  };

  /**
   * Get size for build
   * @returns {Object}
   */
  const getBuildSize = () => ({
    ...tacHelpers.getSize(tac, null, {
      realArticles: true,
    }),
    locale: applicationSettings.locale,
  });

  /**
   * Get safety instructions
   * @returns {Object}
   */
  const getSafetyInfo = () => {
    if (!hasTipOverWarning) {
      return undefined;
    }

    return {
      title: translate(t.PRODUCT_DETAILS_SAFETY_AND_COMPLIANCE_HEADER),
      description: translate(t.SAFETY_AND_COMPLIANCE_DESCRIPTION),
    };
  };

  /**
   *  Get custom tab(s) for range
   * @returns {Object}
   */
  const getCustomTabs = () => {
    // If any other range gets custom tabs this needs updating.
    const allItemsInTac = tacHelpers.getAllItems(tac.items) as TacItem[];
    const drawerFound = !!allItemsInTac.find(i =>
      productService.isType(i, ITEMS.DRAWER)
    );

    if (applicationSettings.applicationName === RANGES.AURDAL && drawerFound) {
      return [{ label: translate(t.PRODUCT_DETAILS_GOOD_TO_KNOW_HEADER) }];
    }

    return [];
  };

  const getStoreAvailabilities = () =>
    storeAvailabilities?.length ? storeAvailabilities : undefined;

  const getSkyttaUrl = (): string | undefined => {
    if (!vpcCode || !locale) return;

    let skyttaUrl;
    if (applicationSettings.isProd) {
      const currentUrl = window.location.href;
      const DELIMITER = '/addon-app/';
      const firstPartOfUrl = currentUrl.includes(DELIMITER)
        ? currentUrl.split(DELIMITER)[0]
        : null;

      if (!firstPartOfUrl) return;

      skyttaUrl = `${firstPartOfUrl}${DELIMITER}skytta/web/latest/?uiPlatform=web&locale=${locale}&storageDesignCode=${vpcCode}#/planner`;
    } else {
      // Fallback URL when not run in production.
      skyttaUrl = 'https://www.ikea.com/';
    }

    return skyttaUrl;
  };

  const getLinkCard = (): ILinkCard | undefined => {
    const skyttaUrl = getSkyttaUrl();
    if (!skyttaUrl) return;

    const linkCard: ILinkCard = {
      imageUrl: `${constants.IMAGE_ROOT}${constants.LINK_CARD_SKYTTA_IMAGE_FILENAME}`,
      titleText: translate(t.LINK_CARD_SKYTTA_HEADING),
      bodyTextLines: [translate(t.LINK_CARD_SKYTTA_DESCRIPTION)],
      buttonText: translate(t.LINK_CARD_SKYTTA_BUTTON),
      linkUrl: skyttaUrl,
      meta: {
        vpcCode,
      },
    };

    return linkCard;
  };

  const readyToBeDisplayed =
    // VPC save upon entry should be finished (success or failure) and design link generated upon success.
    isDesignLinkEitherReadyOrFailed ||
    // Reattempted VPC save might be in progress when the user has clicked one of the buttons in summary, and
    // this should not hide the summary page despite the above criterion temporarily not being fulfilled.
    isAnyUserActionInProgress;

  return readyToBeDisplayed && dexfSettings ? (
    <React.Fragment>
      <TopBar className={topBarStyles.TopBarSummaryView} />
      <div className={styles.SummaryPage}>
        <KompisSummaryPage
          theme={SkapaTheme}
          locale={locale}
          disableLinks={isKiosk}
          defaultSortBy={SortListOptionsEnum.aisleLocation}
          showShareDesign={true}
          showDesignText={true}
          customTabButtons={getCustomTabs()}
          showTotalPackageWeight={true}
          showAddToBag={!isKiosk}
          stores={stores}
          storeId={storeId}
          vpcCode={vpcCode}
          screenshotUrl={detailedSceneImage}
          shareDesignState={saveVpcState}
          visibleModal={getModal()}
          addToCartState={addToCartState}
          addToListState={addToListState}
          shoppingProducts={shoppingProducts}
          translations={kompisTranslations}
          settings={dexfSettings}
          buyInStore={{ buyInStoreLink: designLink }}
          analytics={{
            dev: !applicationSettings.isProd,
            ab: true, // set to true to enable automatic ab tests.
          }}
          productName={applicationSettings.applicationName}
          storeAvailabilities={getStoreAvailabilities()}
          productMeasures={ProductRowProductMeasureEnum.long}
          configurationMeasures={SummaryPageConfigurationMeasureEnum.long}
          measurements={getBuildSize()}
          confirmationCardType={
            confirmationCardType ? confirmationCardType : undefined
          }
          addUnavailableProductsToList={addUnavailableProductsToList}
          failedShoppingItems={
            failedShoppingItems ? failedShoppingItems : undefined
          }
          designLink={designLink ? designLink : undefined}
          shareDesignCard={shareDesignCard}
          securityInformation={getSafetyInfo()}
          collectableProducts={isMobile}
          collectedProducts={collectedProducts}
          startHeadingsAtH2={true}
          enableMargins={true}
          homeDelivery={{
            zipAvailabilities: zipAvailabilities,
          }}
          financingOption={financingOption}
          linkCard={getLinkCard()}
          onFinancialServicesContinueButtonClick={setHideModal}
          onOpenZipIn={onOpenZipIn}
          onSaveZipCode={onHomeDeliverySubmitButtonClick}
          onForgetZipCode={onHomeDeliveryForgetClick}
          zipCode={zipCodeExists}
          zipInCardState={zipInCardState}
          onCollectProduct={onCollectProduct}
          onShareDesign={onShareDesign}
          onAddToCart={onAddToCart}
          onAddToList={onAddToList}
          onModalOpen={onModalOpen}
          onModalClose={setHideModal}
          onSortListChange={setHideModal}
          onAddUnavailableToCartAndList={onAddUnavailableToCartAndList}
          onStoreSelectorChange={onStoreSelectorChange}
          onAddUnavailableProductsToListChange={
            onAddUnavailableProductsToListChange
          }
          onShareDesignCardStateChange={onShareDesignCardStateChange}
          onCopyDesignCodeClick={onCopyDesignCodeClick}
          onCopyDesignCodeReset={onCopyDesignCodeReset}
          onCopyDesignLinkClick={onCopyDesignLinkClick}
          onCopyDesignLinkReset={onCopyDesignLinkReset}
          onKioskMainCtaButtonClick={onKioskMainCtaButtonClick}
          onSendByEmailSend={onSendByEmailSend}
          onSendByEmailReset={onSendByEmailReset}
        >
          <GoodToKnowTab />
        </KompisSummaryPage>
      </div>
      {!isKiosk && <Ymal />}
      {isKiosk && (
        <KompisSummaryPageFooter enableMargins={true} theme={SkapaTheme} />
      )}
    </React.Fragment>
  ) : (
    <LoadingSpinner />
  );
};

export default SummaryPage;
